import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { playerDashboardApi } from '../../services/api';

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const STATUS_OPTIONS = [
  { value: 'all',         label: 'All' },
  { value: 'active',      label: 'Active' },
  { value: 'completed',   label: 'Completed' },
];

const ACTIVE_STATUSES = ['registration', 'seeding', 'draw_ready', 'in_progress'];

function statusBadge(status) {
  const map = {
    registration:  { label: 'Registration Open', bg: '#eff6ff', color: '#1d4ed8' },
    seeding:       { label: 'Seeding',            bg: '#fefce8', color: '#854d0e' },
    draw_ready:    { label: 'Draw Ready',          bg: '#f0fdf4', color: '#15803d' },
    in_progress:   { label: 'Live',               bg: '#fef3c7', color: '#92400e' },
    completed:     { label: 'Completed',           bg: '#f3f4f6', color: '#6b7280' },
    cancelled:     { label: 'Cancelled',           bg: '#fef2f2', color: '#dc2626' },
  };
  const s = map[status] || { label: status, bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const MyTournaments = () => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    playerDashboardApi.getMyTournaments()
      .then(res => setTournaments(res.data.tournaments || []))
      .catch(err => console.error('MyTournaments load error:', err))
      .finally(() => setLoading(false));
  }, []);

  const filtered = tournaments.filter(t => {
    if (filter === 'active') return ACTIVE_STATUSES.includes(t.status);
    if (filter === 'completed') return t.status === 'completed';
    return true;
  });

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner" /></div>;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', margin: 0 }}>My Tournaments</h1>
        <div style={{ display: 'flex', gap: 6 }}>
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              style={{
                padding: '5px 14px', borderRadius: 99, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', border: 'none',
                background: filter === opt.value ? '#0D9488' : '#f3f4f6',
                color: filter === opt.value ? 'white' : '#374151',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '3rem', textAlign: 'center' }}>
          {tournaments.length === 0 ? (
            <>
              <p style={{ color: '#374151', fontWeight: 600, marginBottom: 4 }}>You haven't entered any tournaments yet.</p>
              <Link
                to="/tournaments"
                style={{ display: 'inline-block', marginTop: '1rem', padding: '8px 20px', background: '#0D9488', color: 'white', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none' }}
              >
                Find Tournaments
              </Link>
            </>
          ) : (
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>No tournaments match this filter.</p>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filtered.map(t => (
            <div key={t.id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem 1.5rem' }}>
              {/* Tournament header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>{t.name}</span>
                    {statusBadge(t.status)}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {t.sport_type && <span style={{ textTransform: 'capitalize' }}>{t.sport_type.replace('_', ' ')} · </span>}
                    {t.location && <span>{t.location} · </span>}
                    {fmt(t.start_date)}{t.end_date && t.end_date !== t.start_date ? ` – ${fmt(t.end_date)}` : ''}
                  </div>
                </div>
                <Link
                  to={`/t/${t.id}`}
                  style={{ fontSize: '0.8rem', color: '#0D9488', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  View Tournament →
                </Link>
              </div>

              {/* Entry rows */}
              <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
                  My Entries
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {collapseEntries(t.entries).map((e, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#374151' }}>{e.event_name}</span>
                      <span style={{ color: '#9ca3af' }}>·</span>
                      <span style={{ color: '#6b7280', textTransform: 'capitalize' }}>{e.entry_type}</span>
                      {e.seed_number && (
                        <span style={{ fontSize: '0.72rem', background: '#fef9c3', color: '#854d0e', borderRadius: 99, padding: '1px 7px', fontWeight: 600 }}>
                          Seed {e.seed_number}
                        </span>
                      )}
                      {e._partner && (
                        <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>w/ {e._partner}</span>
                      )}
                      {entryStatusBadge(e.entry_status)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Collapse doubles pairs into one display row
function collapseEntries(entries) {
  const seen = new Set();
  const result = [];
  for (const e of entries) {
    if (e.pair_id) {
      if (seen.has(e.pair_id)) continue;
      seen.add(e.pair_id);
      const partner = entries.find(x => x.pair_id === e.pair_id && x.player_entry_id !== e.player_entry_id);
      result.push({ ...e, _partner: partner?.player_name || null });
    } else {
      result.push(e);
    }
  }
  return result;
}

function entryStatusBadge(status) {
  if (!status || status === 'active') return null;
  const map = {
    waitlisted: { label: 'Waitlisted', bg: '#fef3c7', color: '#92400e' },
    withdrawn:  { label: 'Withdrawn',  bg: '#fef2f2', color: '#dc2626' },
  };
  const s = map[status];
  if (!s) return null;
  return (
    <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '1px 7px', borderRadius: 99, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export default MyTournaments;
